import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  Provider,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbilityService } from 'ability';
import { I18NextPipe, I18NextService } from 'angular-i18next';
import {
  AlertComponent,
  ButtonComponent,
  CardComponent,
  CardGroupComponent,
  FormGroupComponent,
  FormsUtil,
  MbsSize,
  MbsValidators,
  ModalService,
  ModalSettings,
  RadioComponent,
  SwitcherComponent,
  TileComponent
} from 'mbs-ui-kit';
import { FullBackupData, IncrementalData, ScheduleStepValue, ScheduleType } from 'mbs-ui-shared@models/backup/schedule-models';
import { stopAfterMinutesValidator } from 'mbs-ui-shared@utils/validators';
import { BehaviorSubject, noop, ObservedValueOf } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { StepsHelpers } from 'wizards/helpers/steps-helpers';
import { ForceValidType } from 'wizards/models/base/other-base-models/force-valid-type';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import { WizardStepsService } from 'wizards/services/wizard-steps.service';
import { RetentionPolicyControlPanelComponent } from 'wizards/steps/retention-policy-step/NBF/control-panel/retention-policy-control-panel.component';
import { NBFAdditionalModalComponent } from 'wizards/steps/schedule-step/NBF/additional-modal/additional-modal.component';
import { NBFScheduleModalComponent } from 'wizards/steps/schedule-step/NBF/schedule-modal/schedule-modal.component';
import { ScheduleStepBase } from 'wizards/steps/Schedule.StepBase.class';
import { AgentVersions } from 'wizards/steps/StepBase.class';
import {
  daysOfWeekEnumArray,
  getAdvancedScheduleDataInfo,
  getDefaultFullSchedule,
  getDefaultIncrementalSchedule,
  getDefaultSyntheticSchedule,
  getScheduleDataInfo,
  isSelectedDaysValidator,
  weekNumberEnumArray
} from './schedule-step.component.utils';

const NBFScheduleStepValueAccessor: Provider = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  useExisting: forwardRef(() => NBFScheduleStepComponent),
  multi: true
};

@UntilDestroy()
@Component({
  selector: 'mbs-nbf-schedule-step',
  templateUrl: './schedule-step.component.html',
  styleUrls: ['./schedule-step.component.scss'],
  providers: [NBFScheduleStepValueAccessor],
  imports: [
    AlertComponent,
    ButtonComponent,
    CardComponent,
    CardGroupComponent,
    CommonModule,
    FormGroupComponent,
    I18NextPipe,
    NgbTooltipModule,
    RadioComponent,
    ReactiveFormsModule,
    SwitcherComponent,
    TileComponent,
    RetentionPolicyControlPanelComponent
  ]
})
export class NBFScheduleStepComponent extends ScheduleStepBase<ScheduleStepValue> implements OnInit, OnChanges {
  protected readonly MbsSize = MbsSize;

  private modalService = inject(ModalService);
  ability = inject(AbilityService);
  mainService: RemoteManagementWizardsService;
  protected stepService: WizardStepsService;
  protected i18nextService: I18NextService;

  @Input() retentionTitle: string;
  @Input() showSyntheticFull = true;
  @Input() stepFocused = false;
  @Input() showOverdue = true;

  @Output() selectStep = new EventEmitter<string>();
  @Output() changeType = new EventEmitter<keyof typeof ScheduleType>();

  @ViewChild('FFIPayAttentionGFS', { static: true, read: TemplateRef }) FFIPayAttentionGFS: TemplateRef<HTMLElement>;

  public readonly elementSelectors = {
    id: {
      rootDiv: 'NBF-Schedule-root-div',
      defaultModeBlock: 'NBF-Schedule-default-mode-block',
      oldAgentForFFIAlert: 'NBF-Schedule-old-agent-for-FFI-alert',
      invalidFFIAlert: 'NBF-Schedule-invalid-FFI-alert',
      tileSimple: 'NBF-Schedule-tile-simple',
      tileAdvanced: 'NBF-Schedule-tile-advanced',
      noSchedule: 'NBF-Schedule-checkbox-no-schedule',
      simpleModeBlock: 'NBF-Schedule-simple-mode-block',
      buttonSimple: 'NBF-Schedule-button-simple',
      syntheticBlock: 'NBF-Schedule-synthetic-block',
      buttonSynthetic: 'NBF-Schedule-button-synthetic',
      advancedModeBlock: 'NBF-Schedule-advanced-mode-block',
      switcherAdvanced: 'NBF-Schedule-switcher-advanced',
      switcherAdvancedFull: 'NBF-Schedule-switcher-advanced-full',
      buttonAdvanced: 'NBF-Schedule-button-advanced',
      buttonFull: 'NBF-Schedule-button-full',
      alertFull: 'NBF-Schedule-alert-full',
      additionalModeBlock: 'NBF-Schedule-additional-mode-block',
      notScheduleAlert: 'NBF-Schedule-not-full-alert'
    }
  };

  public retention: ObservedValueOf<ReturnType<typeof this.stepService.pipeRetention>> = {};
  public invalidScheduleTypeSelection = false;
  public syntheticRequired = false;

  public scheduleInfo: { simple: string; incremental: string; full: string; synthetic: string } = {
    simple: '',
    incremental: '',
    full: '',
    synthetic: ''
  };

  public canOffFull: boolean;

  private $deactivateSimple = new BehaviorSubject<boolean>(false);
  private initialScheduleType: string;
  private isFFIConfirmed = false;
  private askModalActive = false;

  public get notConfiguredFull(): boolean {
    if (this.canOffFull) {
      return false;
    }

    return this.isAdvancedMode && !this.stepForm.get('fullBackupData').get('enabled').value;
  }

  public get isSimpleMode(): boolean {
    return this.stepForm?.get('ScheduleType').value === 'ffi';
  }

  public get isManualMode(): boolean {
    return this.stepForm?.get('ScheduleType').value === 'noschedule';
  }

  public get isAdvancedMode(): boolean {
    return this.stepForm?.get('ScheduleType').value === 'predefined';
  }

  public get syntheticDayOutsideBackup(): boolean {
    if (!this.isSimpleMode || !this.isFfiSyntheticFullEnabled) {
      return false;
    }

    const incremental = this.stepForm.get('incrementalData').value;

    if (incremental.recurringPeriod !== 'Daily') {
      return false;
    }

    return !(incremental.weeklyDayOfWeek || [])[this.stepForm.get('syntheticData').get('dayOfWeek').value];
  }

  private get needSyntheticSchedule(): boolean {
    return (this.isSimpleMode && this.syntheticRequired) || this.syntheticDayOutsideBackup;
  }

  constructor() {
    const mainService = inject(RemoteManagementWizardsService);
    const stepService = inject(WizardStepsService);
    const i18nextService = inject(I18NextService);

    super(mainService, stepService, i18nextService);
    const ability = this.ability;
    this.mainService = mainService;
    this.stepService = stepService;
    this.i18nextService = i18nextService;

    this.canOffFull = ability.can('read', 'AllowNbfScheduleWithoutFullBackups');
  }

  ngOnInit(): void {
    this.initForm();
    this.initEvents();
  }

  initForm(): void {
    super.initForm();

    this.stepForm = new UntypedFormGroup(
      {
        ScheduleType: new FormControl(''),
        ScheduleTypeNone: new FormControl(false), // Virtual property
        specificDateGroup: new UntypedFormGroup({
          date: new FormControl(),
          time: new FormControl('00:00', MbsValidators.timeValidatorWithoutText)
        }),
        StopAfterEnabled: new FormControl(false),
        stopAfterHours: new FormControl(0),
        stopAfterMinutes: new FormControl(0, (control) => {
          if (this.stepForm?.get('StopAfterEnabled').value) {
            return stopAfterMinutesValidator.bind(this, 'stopAfterHours')(control);
          }

          return null;
        }),
        ForceMissedSchedule: new FormControl(false),
        overdueAfterSuccessEnabled: new FormControl(false),
        overdueAfterSuccessAmount: new FormControl(1, Validators.min(1)),
        overdueAfterSuccessPeriod: new FormControl(0),
        incrementalData: new UntypedFormGroup(getDefaultIncrementalSchedule()),
        fullBackupData: new UntypedFormGroup(getDefaultFullSchedule()),
        syntheticData: new UntypedFormGroup(getDefaultSyntheticSchedule())
      },
      this.isSelectedDaysValidator.bind(this)
    );

    this.initFormEvents();
  }

  onStepFormChange(value: ScheduleStepValue): void {
    const scheduleType = this.stepForm.get('ScheduleType').value;
    const badStorage = scheduleType === 'ffi' && this.hasFFIProblem;
    const correctSchedule = !this.notConfiguredFull && !scheduleType;
    const scheduleSelected = !!scheduleType;
    const needConfirmFFI = this.getNeedConfirmFFI();

    this.value = {
      ...value,
      valid:
        this.stepForm.valid &&
        !badStorage &&
        !correctSchedule &&
        scheduleSelected &&
        !this.notConfiguredFull &&
        !needConfirmFFI &&
        !this.needSyntheticSchedule
    };

    this.updateScheduleInfo();
    this.stepService.updateProperty('schedule', 'isSimpleMode', this.isSimpleMode);
    this.stepService.updateProperty('schedule', 'isAdvancedMode', this.isAdvancedMode);
    this.stepService.updateProperty('schedule', 'isManualMode', this.isManualMode);

    if (this.notConfiguredFull) {
      return void this.addStepMark.emit({
        stepName: this.title,
        actions: { save: { description: this.i18nextService.t('wizards:schedule.advancedMode.fullNotConfiguredInfo'), important: true } }
      });
    }

    this.removeStepMark.emit({ stepName: this.title, actions: ['save'] });
  }

  ngOnChanges() {
    queueMicrotask(() => this.updateScheduleInfo());
  }

  updateForm(value: ScheduleStepValue): void {
    this.syntheticRequired = !!value.syntheticRequired && this.isFfiSyntheticFullEnabled;

    this.stepForm.reset(value);
    this.stepForm.get('ScheduleTypeNone').setValue(this.stepForm.get('ScheduleType').value === 'noschedule');

    if (!value.syntheticData) {
      this.stepForm.get('syntheticData').reset(new UntypedFormGroup(getDefaultSyntheticSchedule()).value);
    }

    this.initialScheduleType = value.ScheduleType;
    this.stepService.updateProperty('schedule', 'incrementalSchedule', this.stepForm.get('incrementalData').value);
    this.stepService.updateProperty('schedule', 'fullSchedule', this.stepForm.get('fullBackupData').value);

    if (this.isEdit) {
      this.checkSchedule();
      this.catchBrokenFFI();
    }

    StepsHelpers.updateDaysOfWeek(value.incrementalData.weeklyDayOfWeek, this.stepForm.get('incrementalData') as UntypedFormGroup);
    StepsHelpers.updateDaysOfWeek(value.fullBackupData.weeklyDayOfWeek, this.stepForm.get('fullBackupData') as UntypedFormGroup);
  }

  forceValid(data: ForceValidType = null): void {
    const needConfirmFFI = this.getNeedConfirmFFI();

    this.checkSchedule();

    if (needConfirmFFI) {
      this.showGFSAlert(data.isNext, data.isSave);
    } else if (this.needSyntheticSchedule) {
      this.askSyntheticSchedule(data?.isNext, data?.isSave);
    }

    FormsUtil.triggerValidation(this.stepForm);
    this.resetValidStateForValidFields();
  }

  private askBeforeActivateTitle(value: keyof typeof ScheduleType): void {
    if (this.askModalActive) {
      return;
    }

    const resetNoSchedule = () => this.stepForm.get('ScheduleTypeNone').setValue(false);
    this.askModalActive = true;

    this.modalService
      .open(
        {
          header: { title: this.i18nextService.t('wizards:GFSRequiresTitle') },
          footer: { okButton: { text: this.i18nextService.t('wizards:GFSRequiresDisableBtn') } }
        },
        this.i18nextService.t('wizards:GFSRequiresText')
      )
      .then((result) => {
        if (result) {
          this.activateTile(value, true);
        } else if (value === 'noschedule') {
          resetNoSchedule();
        }
      })
      .catch(resetNoSchedule)
      .finally(() => (this.askModalActive = false));
  }

  activateTile(value: keyof typeof ScheduleType, force = false): void {
    if (!force && this.retention.isGFSMode && ['noschedule', 'ffi'].includes(value)) {
      return this.askBeforeActivateTitle(value);
    }

    if (value === 'ffi' && (this.hasFFIProblem || !this.isFFIEnabled)) {
      return;
    }

    this.stepForm.get('ScheduleType').setValue(value);
    this.stepForm.get('ScheduleTypeNone').setValue(value === 'noschedule');
    this.changeType.emit(value);
    this.invalidScheduleTypeSelection = false;

    if (value === 'ffi') {
      this.stepForm.get('ScheduleTypeNone').setValue(false);
      this.stepForm.get('incrementalData').get('enabled').setValue(true);
      this.stepForm.get('fullBackupData').get('enabled').setValue(false);
      this.provideWeeklyDayOfWeek(['incrementalData']);

      this.stepService.updateProperty('schedule', 'incrementalSchedule', this.stepForm.get('incrementalData').value);
    }

    if (value == 'predefined') {
      this.stepForm.get('ScheduleTypeNone').setValue(false);
      this.stepForm.get('fullBackupData').get('enabled').setValue(true);
      this.provideWeeklyDayOfWeek(['incrementalData', 'fullBackupData']);

      const full = this.stepForm.get('fullBackupData');
      const validVersion = this.isRDMode || this.mainService.validVersionForSchedule;

      if (validVersion && !full.get('repeatEveryCount').value && (!this.isLinux || full.get('recurringPeriod').value === 'Monthly')) {
        full.get('repeatEveryCount').setValue(1);
      }

      this.stepService.updateProperty('schedule', 'fullSchedule', this.stepForm.get('fullBackupData').value);
    }

    if (value === 'noschedule') {
      this.stepForm.get('incrementalData').get('enabled').setValue(false);
      this.stepForm.get('fullBackupData').get('enabled').setValue(false);
    }
  }

  clickRetention(event: string): void {
    if (event === 'openRetention') {
      this.selectStep.emit(this.retentionTitle);
    }
  }

  clickDetails(mode: 'simple' | 'advanced' | 'full'): void {
    this.openScheduleModal(mode);
  }

  private openScheduleModal(mode: 'simple' | 'advanced' | 'full'): Promise<void> {
    // eslint-disable-next-line @typescript-eslint/ban-types
    let final: Function = noop;
    const settings: ModalSettings = {
      ...new ModalSettings(),
      size: MbsSize.md,
      data: { mode, isUnix: this.isUnix && !this.isNetBuild, showSynthetic: this.showSyntheticFull }
    };

    if (mode === 'simple' || mode === 'advanced') {
      settings.data.schedule = { ...this.stepForm.get('incrementalData').value };

      if (mode === 'simple') {
        settings.data.canSynthetic = this.isFfiSyntheticFullEnabled;
        settings.data.syntheticRequired = this.syntheticRequired;
        settings.data.syntheticSchedule = { ...this.stepForm.get('syntheticData').value };
        settings.data.syntheticVersion = AgentVersions.FfiSyntheticFull;
      }

      final = (data) => {
        const { syntheticData, ...schedule } = data;

        this.stepForm.get('incrementalData').reset(schedule);

        if (syntheticData) {
          this.syntheticRequired = false;
          this.stepForm.get('syntheticData').reset(syntheticData);
        }

        this.stepService.updateProperty('schedule', 'incrementalSchedule', this.stepForm.get('incrementalData').value);
      };
    }

    if (mode === 'full') {
      settings.data.schedule = { ...this.stepForm.get('fullBackupData').value };
      settings.data.incrementalSchedule = { ...this.stepForm.get('incrementalData').value };

      final = (data) => {
        this.stepForm.get('fullBackupData').reset({ ...data, enabled: true }); // TODO: Test
        this.stepService.updateProperty('schedule', 'fullSchedule', this.stepForm.get('fullBackupData').value);
      };
    }

    return this.modalService
      .openCustom(NBFScheduleModalComponent, settings)
      .then((result: { data: IncrementalData | FullBackupData } | null) => {
        if (!result) {
          return;
        }

        final(result.data);
      })
      .catch(noop);
  }

  incrementalEnabledChangeHandler(): void {
    const value = !this.stepForm.get('incrementalData').get('enabled').value;

    this.stepForm.get('incrementalData').get('enabled').setValue(value);
  }

  fullEnabledChangeHandler(): void {
    const value = !this.stepForm.get('fullBackupData').get('enabled').value;

    this.stepForm.get('fullBackupData').get('enabled').setValue(value);

    if (!value && this.isAdvancedMode) {
      this.stepForm.get('incrementalData').get('enabled').setValue(true);
    }
  }

  clickAdditional(): void {
    const propertiesMap = ['StopAfterEnabled', 'stopAfterHours', 'stopAfterMinutes', 'ForceMissedSchedule'];
    const showOverdue = this.showOverdue && !this.isRDMode;

    if (showOverdue) {
      propertiesMap.push('overdueAfterSuccessEnabled', 'overdueAfterSuccessAmount', 'overdueAfterSuccessPeriod');
    }

    const settings: ModalSettings = {
      ...new ModalSettings(),
      size: MbsSize.md,
      data: {
        isManualMode: this.isManualMode,
        showOverdue,
        startFromDate: this.stepForm.get('incrementalData').get('startFromDate').value
      }
    };

    propertiesMap.forEach((key) => {
      settings.data[key] = this.stepForm.get(key).value;
    });

    this.modalService
      .openCustom(NBFAdditionalModalComponent, settings)
      .then((result: { data: Record<string, unknown> }) => {
        if (!result.data) {
          return;
        }

        // TODO: Test
        // New startFromDate must be applied to both schedules
        this.stepForm.get('incrementalData').get('startFromDate').setValue(result.data.startFromDate);
        this.stepForm.get('fullBackupData').get('startFromDate').setValue(result.data.startFromDate);

        propertiesMap.forEach((key) => {
          this.stepForm.get(key).setValue(result.data[key]);
        });
      })
      .catch(noop);
  }

  protected deactivateSimple() {
    if (this.isSimpleMode) {
      this.activateTile('predefined');

      this.$deactivateSimple.next(true);
    }
  }

  private provideWeeklyDayOfWeek(groupNames: string[]): void {
    groupNames.forEach((name) => {
      const group = this.stepForm.get(name);

      if (group.get('weeklyDayOfWeek').value.some((day) => !!day)) {
        return;
      }

      if (name === 'incrementalData') {
        const template = new UntypedFormGroup(getDefaultIncrementalSchedule());

        return void group.get('weeklyDayOfWeek').reset(template.get('weeklyDayOfWeek').getRawValue());
      }

      const template = new UntypedFormGroup(getDefaultFullSchedule());

      group.get('weeklyDayOfWeek').reset(template.get('weeklyDayOfWeek').getRawValue());
    });
  }

  private checkSchedule(): void {
    const value = this.stepForm.get('ScheduleType').value;
    const scheduleSelected = ['noschedule', 'ffi', 'predefined'].includes(value);

    if (!scheduleSelected) {
      this.invalidScheduleTypeSelection = true;
      this.value = { ...this.value, valid: false };

      return;
    }

    this.invalidScheduleTypeSelection = false;
  }

  private updateScheduleInfo(): void {
    if (this.isSimpleMode) {
      this.scheduleInfo = {
        simple: this.combineScheduleInfo('incrementalData'),
        incremental: '',
        full: '',
        synthetic: this.combineSyntheticInfo()
      };

      return void this.stepService.updateProperty('schedule', 'scheduleInfo', this.scheduleInfo);
    }

    if (this.isAdvancedMode) {
      this.scheduleInfo = {
        simple: '',
        incremental: this.combineScheduleInfo('incrementalData'),
        full: this.combineScheduleInfo('fullBackupData'),
        synthetic: ''
      };

      return void this.stepService.updateProperty('schedule', 'scheduleInfo', this.scheduleInfo);
    }

    this.scheduleInfo = {
      simple: '',
      incremental: '',
      full: '',
      synthetic: ''
    };

    return void this.stepService.updateProperty('schedule', 'scheduleInfo', this.scheduleInfo);
  }

  protected initEvents(): void {
    super.initEvents();

    this.stepForm
      .get('incrementalData')
      .get('enabled')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe((value) => {
        this.stepService.updateProperty('schedule', 'incrementalDataEnabled', value);
      });

    this.stepService
      .pipeProperty('retention')
      .pipe(untilDestroyed(this))
      .subscribe((data) => {
        const isGFSChanged = this.retention.isGFSMode !== data.isGFSMode;

        this.retention = data;

        if (isGFSChanged && data.isGFSMode) {
          this.activateTile('predefined');
        }
      });
  }

  private catchBrokenFFI(): void {
    if (!this.isSimpleMode) {
      return;
    }

    const interact = this.stepService.pipeProperty('wizard', 'userInteractWithSteps');

    this.$deactivateSimple.pipe(takeUntil(interact)).subscribe((result) => {
      if (result) {
        this.stepService.updateProperty('schedule', 'forceDeactivateFFI', true);
      }
    });
  }

  private combineScheduleInfo(type: 'incrementalData' | 'fullBackupData'): string {
    if (this.notConfiguredFull && type === 'fullBackupData') {
      return this.i18nextService.t('wizards:schedule.advancedMode.fullNotConfigured');
    }

    const value = this.stepForm.get(type).value;

    return this.isAdvancedMode
      ? getAdvancedScheduleDataInfo(value, type, this.i18nextService)
      : getScheduleDataInfo(value, this.i18nextService);
  }

  private combineSyntheticInfo(): string {
    if (this.stepForm.get('incrementalData').get('recurringPeriod').value !== 'Daily') {
      return this.i18nextService.t('wizards:schedule.scheduleModal.synthetic.monthlyNote');
    }

    const value = this.stepForm.get('syntheticData').value;
    const dayOfWeekText = (daysOfWeekEnumArray.find((item) => item.value === value.dayOfWeek) || { label: '' }).label;

    if (value.recurringPeriod === 'Weekly') {
      return `Weekly on: ${dayOfWeekText}`;
    }

    const occurrenceText = String(
      (weekNumberEnumArray.find((item) => item.value === value.occurrence) || { label: '' }).label
    ).toLowerCase();

    return `Monthly on: ${occurrenceText} ${dayOfWeekText}`;
  }

  private isSelectedDaysValidator(formGroup: UntypedFormGroup): ValidationErrors | null {
    return isSelectedDaysValidator(formGroup, this.isNBF, this.isRestore);
  }

  private showGFSAlert(needNextStep: boolean, needSave: boolean): void {
    const modalSettings: ModalSettings = {
      header: { title: this.i18nextService.t('wizards:ffi_warning_title', { format: 'title' }) },
      footer: {
        okButton: { text: this.i18nextService.t('shared:buttons.enable') },
        cancelButton: { text: this.i18nextService.t('shared:buttons.cancel') }
      }
    };

    this.modalService
      .open(modalSettings, this.FFIPayAttentionGFS)
      .then((result) => {
        if (result) {
          this.isFFIConfirmed = true;
          this.stepForm.updateValueAndValidity();

          if (needNextStep || needSave) this.nextStep.emit(needSave);
        }
      })
      .catch(noop);
  }

  private askSyntheticSchedule(needNextStep: boolean, needSave: boolean): void {
    this.openScheduleModal('simple').then(() => {
      if (this.needSyntheticSchedule) {
        return;
      }

      if (needNextStep || needSave) this.nextStep.emit(needSave);
    });
  }

  private getNeedConfirmFFI(): boolean {
    return (
      (!this.isUnix || this.isNetBuild) &&
      !this.isCreate &&
      !this.isFFIConfirmed &&
      this.initialScheduleType !== 'ffi' &&
      this.initialScheduleType !== 'noschedule' &&
      this.stepForm.get('ScheduleType').value === 'ffi'
    );
  }
}
